import { Injectable } from '@angular/core';
import { DialogService, NetworkHelper, NotifierService } from '@wm-libs/core';
import { BehaviorSubject, Observable, take, tap } from 'rxjs';
import { environment } from '../../../environments/environment';
import { Account, ConnectDeviceData } from '../interfaces//accounts.interface';

@Injectable({
  providedIn: 'root',
})
export class AccountsService {
  readonly apiUrl = `${environment.poolApi}/accounts`;

  private _accounts = new BehaviorSubject<Account[] | null>(null);
  readonly accounts$ = this._accounts.asObservable();

  private _selectedAccount = new BehaviorSubject<Account | null>(null);
  readonly selectedAccount$ = this._selectedAccount.asObservable();

  constructor(private networkHelper: NetworkHelper, private notifier: NotifierService, private dialogService: DialogService) {}

  getAccounts(): Observable<Account[]> {
    return this.networkHelper.get<Account[]>(`${this.apiUrl}/list`).pipe(
      take(1),
      tap((accounts) => this.setAccounts(accounts))
    );
  }

  createAccount(label: string): Observable<Account[]> {
    return this.networkHelper.post<Account[]>(`${this.apiUrl}/create`, { label }).pipe(
      tap((accounts) => {
        if (accounts && accounts.length > 0) {
          const lastAccount = accounts[accounts.length - 1];
          if (lastAccount && lastAccount.id) {
            this.switchAccount(lastAccount.id).subscribe();
          }
        }
      })
    );
  }

  updateAccount(label: string, id: number): Observable<Account[]> {
    return this.networkHelper.post<Account[]>(`${this.apiUrl}/update/${id}`, { label }).pipe(tap((accounts) => this.setAccounts(accounts)));
  }

  switchAccount(id: number): Observable<Account[]> {
    return this.networkHelper.post<Account[]>(`${this.apiUrl}/switch/${id}`, {}).pipe(
      tap(() => this.notifier.setNotifications('POOL.FARM_SWITCHED')),
      tap((accounts) => this.setAccounts(accounts))
    );
  }

  connect(): Observable<ConnectDeviceData> {
    return this.networkHelper.get<ConnectDeviceData>(`${this.apiUrl}/connect`).pipe(
      tap((data) => {
        const config = {
          title: 'WM_TECH_POOL.CONNECT.DIALOG.TITLE',
          data: data,
          fields: [{ type: 'pool-connect-template' }],
          submit: false,
        };

        this.dialogService.openPopup(config);
      })
    );
  }

  setAccounts(accounts: Account[]): void {
    this._accounts.next(accounts);
  }

  updateCurrentAccountBalance(newBalance: number) {
    const currentAccounts = this._accounts.getValue();

    if (currentAccounts) {
      const updatedAccounts = currentAccounts.map((account) => {
        if (account.current) {
          return { ...account, balance: newBalance };
        }
        return account;
      });

      this.setAccounts(updatedAccounts);
    }
  }
}
